import React from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

export type PageGalleryItem = {
  image: string;
  caption?: string;
  linkUrl?: string;
};

export type PageGallerySectionConfig = {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  layout?: 'grid' | 'masonry';
  gap?: number;
  radius?: number;
  shadow?: 'none' | 'soft' | 'medium';
  aspectRatio?: string; // e.g. '1 / 1', '4 / 3', '16 / 9'
  columns?: {
    xs?: number;
    sm?: number;
    md?: number;
  };
  lightboxEnabled?: boolean;
  items?: PageGalleryItem[];
};

type Props = {
  config?: PageGallerySectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageGallerySection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const cfg: PageGallerySectionConfig = (config && typeof config === 'object') ? config : {};

  const title = typeof cfg.title === 'string' ? cfg.title : '';
  const subtitle = typeof cfg.subtitle === 'string' ? cfg.subtitle : '';
  const layout: 'grid' | 'masonry' = cfg.layout === 'masonry' ? 'masonry' : 'grid';
  const gap = clamp(cfg.gap, 0, 48) || 12;
  const radius = clamp(cfg.radius, 0, 28) || 16;
  const shadow: 'none' | 'soft' | 'medium' = cfg.shadow === 'medium' ? 'medium' : (cfg.shadow === 'none' ? 'none' : 'soft');
  const aspectRatio = (typeof cfg.aspectRatio === 'string' && cfg.aspectRatio.trim()) ? cfg.aspectRatio.trim() : '1 / 1';
  const cols = cfg.columns || {};
  const xsCols = clamp(cols.xs, 1, 4) || 2;
  const smCols = clamp(cols.sm, 1, 6) || 3;
  const mdCols = clamp(cols.md, 1, 8) || 4;
  const columns = isMdUp ? mdCols : (isSmUp ? smCols : xsCols);

  const items = Array.isArray(cfg.items) ? cfg.items.filter(it => it && typeof it === 'object' && typeof (it as any).image === 'string' && (it as any).image) : [];
  const lightboxEnabled = cfg.lightboxEnabled !== false;

  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const resolve = React.useCallback((p?: string) => {
    if (!p) return '';
    return resolvePublicUrl ? resolvePublicUrl(p) : p;
  }, [resolvePublicUrl]);

  if (cfg.enabled === false) return null;

  const openAt = (idx: number) => {
    if (!lightboxEnabled) return;
    setActiveIndex(Math.max(0, Math.min(items.length - 1, idx)));
    setLightboxOpen(true);
  };

  const nav = (dir: -1 | 1) => {
    if (items.length <= 1) return;
    setActiveIndex((prev) => {
      const next = (prev + dir + items.length) % items.length;
      return next;
    });
  };

  const shadowSx = shadow === 'none'
    ? undefined
    : (shadow === 'medium'
      ? '0 18px 46px rgba(2,6,23,0.20)'
      : '0 12px 32px rgba(2,6,23,0.14)');

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        {(title || subtitle) && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {title ? (
              <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.4 }}>{title}</Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{subtitle}</Typography>
            ) : null}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Box sx={{ width: 90, height: 4, borderRadius: 999, bgcolor: 'primary.main', opacity: 0.9 }} />
            </Box>
          </Box>
        )}

        {items.length === 0 ? (
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Typography variant="body2" color="text.secondary">Keine Bilder in dieser Galerie.</Typography>
          </Box>
        ) : (
          <Box
            sx={
              layout === 'masonry'
                ? {
                    columnCount: { xs: xsCols, sm: smCols, md: mdCols },
                    columnGap: `${gap}px`,
                  }
                : {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    gap: `${gap}px`,
                  }
            }
          >
            {items.map((it, idx) => {
              const url = resolve(it.image);
              const clickable = lightboxEnabled || (!!it.linkUrl && typeof it.linkUrl === 'string');
              const onClick = () => {
                if (lightboxEnabled) return openAt(idx);
                if (it.linkUrl) window.open(it.linkUrl, '_blank', 'noopener,noreferrer');
              };

              const tile = (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: `${radius}px`,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    boxShadow: shadowSx,
                    cursor: clickable ? 'pointer' : 'default',
                    transform: 'translateZ(0)',
                    transition: 'transform .25s ease, box-shadow .25s ease, filter .25s ease',
                    '&:hover': clickable
                      ? {
                          transform: 'translateY(-2px)',
                          filter: 'saturate(1.05)',
                          boxShadow: shadow === 'none' ? undefined : '0 22px 54px rgba(2,6,23,0.24)',
                        }
                      : undefined,
                  }}
                  onClick={clickable ? onClick : undefined}
                >
                  <Box sx={{ position: 'relative', width: '100%', aspectRatio }}>
                    <Box component="img" src={url} alt={it.caption || `Gallery ${idx + 1}`} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.55) 100%)', opacity: it.caption ? 1 : 0, transition: 'opacity .2s ease' }} />
                    {it.caption ? (
                      <Box sx={{ position: 'absolute', left: 14, right: 14, bottom: 12 }}>
                        <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 900, textShadow: '0 8px 20px rgba(0,0,0,0.35)' }}>
                          {it.caption}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              );

              if (layout === 'masonry') {
                return (
                  <Box key={`${it.image}-${idx}`} sx={{ breakInside: 'avoid', mb: `${gap}px` }}>
                    {tile}
                  </Box>
                );
              }

              return (
                <Box key={`${it.image}-${idx}`}>
                  {tile}
                </Box>
              );
            })}
          </Box>
        )}
      </Container>

      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: 'black' }}>
            <Box
              component="img"
              src={items[activeIndex] ? resolve(items[activeIndex].image) : ''}
              alt={items[activeIndex]?.caption || 'Image'}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>

          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(255,255,255,0.14)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' } }}
          >
            <CloseIcon />
          </IconButton>

          {items.length > 1 && (
            <>
              <IconButton
                onClick={() => nav(-1)}
                sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.14)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' } }}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => nav(1)}
                sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.14)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' } }}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </>
          )}

          {items[activeIndex]?.caption ? (
            <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 14 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,0,0,0.55)', color: 'white', px: 1.25, py: 0.75, borderRadius: 2, backdropFilter: 'blur(6px)' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{items[activeIndex]?.caption}</Typography>
                {items[activeIndex]?.linkUrl ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => window.open(items[activeIndex]?.linkUrl as string, '_blank', 'noopener,noreferrer')}
                    sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                  >
                    Open
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PageGallerySection;
